//#region src/parse.ts
const INVALID_JSON_ERROR = "That's not valid JSON. Paste the mcpServers block from Claude or Cursor.";
const NO_ENTRY_ERROR = "No server entry found. Expected an \"mcpServers\" object.";
const NO_URL_OR_COMMAND_ERROR = "Entry needs either a \"url\" or a \"command\".";
const remoteError = (host) => `This is a remote MCP server (${host}) — remote MCP servers are added under Integrations.`;
const bridgeError = (host) => `This config proxies to a remote MCP server (${host}) — remote MCP servers are added under Integrations.`;
const ENVELOPE_KEYS = [
	"mcpServers",
	"servers",
	"context_servers",
	"mcp",
	"mcp_servers",
	"extensions"
];
const URL_FIELDS = [
	"url",
	"serverUrl",
	"httpUrl",
	"uri"
];
const ENV_FIELDS = [
	"env",
	"environment",
	"envs"
];
const REMOTE_TYPES = /* @__PURE__ */ new Set([
	"http",
	"streamable-http",
	"streamable_http",
	"streamablehttp",
	"sse",
	"remote",
	"ws",
	"websocket"
]);
const LOCAL_HOSTS = /* @__PURE__ */ new Set([
	"localhost",
	"127.0.0.1",
	"::1",
	"[::1]"
]);
const SHELL_WRAPPERS = /* @__PURE__ */ new Set([
	"cmd",
	"powershell",
	"pwsh",
	"sh",
	"bash",
	"env"
]);
const INTERPRETERS = /* @__PURE__ */ new Set([
	"node",
	"deno",
	"python",
	"python3",
	"py"
]);
const CONTAINER_RUNNERS = /* @__PURE__ */ new Set(["docker", "podman"]);
const BRIDGE_PACKAGES = /* @__PURE__ */ new Set([
	"mcp-remote",
	"mcp-proxy",
	"@modelcontextprotocol/mcp-proxy",
	"supergateway"
]);
function isRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Title-cases a config key (`server-memory` -> `Server Memory`); falls back to the raw key. */
function titleCaseKey(key) {
	const words = key.split(/[-_ ]+/).filter(Boolean);
	return words.length > 0 ? words.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") : key;
}
/** Primitive values survive, coerced to strings; null/objects are dropped. */
function coerceStringMap(value) {
	const out = {};
	if (!isRecord(value)) return out;
	for (const [k, v] of Object.entries(value)) if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") out[k] = String(v);
	return out;
}
function isPlaceholder(value) {
	return /\$\{[^}]+\}/.test(value) || /^<.+>$/.test(value.trim()) || /YOUR_|CHANGEME|xxxx|_HERE/i.test(value);
}
/** Minimal quote-aware split for bare command strings like `npx -y "@scope/pkg"`. */
function shellSplit(input) {
	const tokens = [];
	let current = "";
	let quote = null;
	for (const ch of input.trim()) if (quote) {
		if (ch === quote) quote = null;
		else current += ch;
	} else if (ch === "\"" || ch === "'") quote = ch;
	else if (/\s/.test(ch)) {
		if (current) tokens.push(current);
		current = "";
	} else current += ch;
	if (current) tokens.push(current);
	return tokens;
}
function hostOf(url) {
	const withScheme = url.includes("://") ? url : `http://${url}`;
	try {
		return new URL(withScheme).hostname;
	} catch {
		return null;
	}
}
function urlPathEndsWithSse(url) {
	const withScheme = url.includes("://") ? url : `http://${url}`;
	try {
		return new URL(withScheme).pathname.replace(/\/$/, "").endsWith("/sse");
	} catch {
		return false;
	}
}
/** Package name without its version pin (`@scope/pkg@1.2` -> `@scope/pkg`). */
function specName(spec) {
	return spec.startsWith("@") ? `@${spec.slice(1).split("@")[0]}` : spec.split("@")[0];
}
function baseName(command) {
	return (command.split(/[\\/]/).pop() ?? command).toLowerCase().replace(/\.(exe|cmd)$/, "");
}
/** Unwrap `cmd /c …`, `sh -c "…"`, `powershell -Command …`, and leading `env K=V …`. */
function unwrapShell(entry) {
	const base = baseName(entry.cmd);
	if (!SHELL_WRAPPERS.has(base)) return entry;
	const { args, env } = entry;
	if (base === "cmd" && (args[0] === "/c" || args[0] === "/k")) {
		const [cmd, ...rest] = args.slice(1);
		return unwrapShell({
			cmd: cmd ?? "",
			args: rest,
			env
		});
	}
	if ((base === "sh" || base === "bash") && args[0] === "-c" && args[1]) {
		const [cmd, ...rest] = shellSplit(args[1]);
		return unwrapShell({
			cmd: cmd ?? "",
			args: rest,
			env
		});
	}
	if (base === "powershell" || base === "pwsh") {
		const i = args.findIndex((a) => a.toLowerCase() === "-command");
		if (i >= 0 && args[i + 1]) {
			const [cmd, ...rest] = shellSplit(args.slice(i + 1).join(" "));
			return unwrapShell({
				cmd: cmd ?? "",
				args: rest,
				env
			});
		}
	}
	if (base === "env") {
		const merged = { ...env };
		let i = 0;
		while (i < args.length && /^[A-Za-z_][A-Za-z0-9_]*=/.test(args[i])) {
			const [k, ...v] = args[i].split("=");
			merged[k] = v.join("=");
			i += 1;
		}
		const [cmd, ...rest] = args.slice(i);
		return unwrapShell({
			cmd: cmd ?? "",
			args: rest,
			env: merged
		});
	}
	return entry;
}
/** npx/bunx/dlx/exec arg walk: skip runner flags, find the package spec. */
function extractNodeSpec(args) {
	let i = 0;
	while (i < args.length) {
		const t = args[i];
		if ([
			"-y",
			"--yes",
			"-q",
			"--quiet",
			"--silent"
		].includes(t)) {
			i += 1;
			continue;
		}
		if (t === "-p" || t === "--package") return {
			spec: args[i + 1] ?? null,
			rest: args.slice(i + 2)
		};
		if (t === "--") return {
			spec: args[i + 1] ?? null,
			rest: args.slice(i + 2)
		};
		break;
	}
	return i < args.length ? {
		spec: args[i],
		rest: args.slice(i + 1)
	} : {
		spec: null,
		rest: []
	};
}
/** uvx/pipx arg walk. */
function extractPySpec(args) {
	const flags = [];
	let i = 0;
	while (i < args.length) {
		const t = args[i];
		if (t === "-q" || t === "--quiet") {
			i += 1;
			continue;
		}
		if (t === "--from") {
			flags.push("uvx-from");
			return {
				spec: args[i + 1] ?? null,
				rest: args.slice(i + 2),
				flags
			};
		}
		if (t === "--spec") return {
			spec: args[i + 1] ?? null,
			rest: args.slice(i + 2),
			flags
		};
		if (t === "-p" || t === "--python") {
			i += 2;
			continue;
		}
		if (t === "--with") {
			flags.push("uvx-with-extra-dep");
			i += 2;
			continue;
		}
		break;
	}
	return i < args.length ? {
		spec: args[i],
		rest: args.slice(i + 1),
		flags
	} : {
		spec: null,
		rest: [],
		flags
	};
}
function envPlaceholderFlags(env) {
	return Object.entries(env).filter(([, v]) => isPlaceholder(v)).map(([k]) => `placeholder-env:${k}`);
}
const SECRET_ARG_RE = /--?(api[-_]?key|access[-_]?token|token|secret)/i;
function parseHttp(key, raw, urlRaw, typeStr) {
	const host = hostOf(urlRaw);
	if (host === "0.0.0.0") return {
		ok: false,
		error: "Use localhost or 127.0.0.1 instead of 0.0.0.0."
	};
	if (!host || !LOCAL_HOSTS.has(host)) return {
		ok: false,
		error: remoteError(host ?? urlRaw)
	};
	const flags = [];
	const headers = coerceStringMap(raw.headers);
	if (!headers.Authorization) for (const field of [
		"bearer_token",
		"api_key",
		"apiKey"
	]) {
		const value = raw[field];
		if (typeof value === "string") {
			headers.Authorization = `Bearer ${value}`;
			flags.push("synthesized-auth-header");
			break;
		}
	}
	if (typeStr === "sse" || urlPathEndsWithSse(urlRaw)) flags.push("sse-endpoint");
	for (const [k, v] of Object.entries(headers)) if (isPlaceholder(v)) flags.push(`placeholder-header:${k}`);
	return {
		ok: true,
		name: titleCaseKey(key),
		transport: {
			kind: "http",
			url: urlRaw,
			...Object.keys(headers).length > 0 && { headers }
		},
		flags
	};
}
function parseLocal(key, entry) {
	const { cmd, args, env } = unwrapShell(entry);
	if (!cmd) return {
		ok: false,
		error: NO_URL_OR_COMMAND_ERROR
	};
	const flags = envPlaceholderFlags(env);
	if (args.some((a) => SECRET_ARG_RE.test(a))) flags.push("secret-in-args");
	const name = titleCaseKey(key);
	const base = baseName(cmd);
	const binary = (extraFlags = []) => ({
		ok: true,
		name,
		transport: {
			kind: "binary",
			command: cmd,
			...args.length > 0 && { args },
			...Object.keys(env).length > 0 && { env }
		},
		flags: [...flags, ...extraFlags]
	});
	const packaged = (runtime, spec, rest, extraFlags = []) => ({
		ok: true,
		name,
		transport: {
			kind: "stdio",
			runtime,
			packageSpec: spec,
			...rest.length > 0 && { args: rest },
			...Object.keys(env).length > 0 && { env }
		},
		flags: [...flags, ...extraFlags]
	});
	if (CONTAINER_RUNNERS.has(base)) return binary();
	if (INTERPRETERS.has(base)) return binary([
		"python",
		"python3",
		"py"
	].includes(base) && args[0] === "-m" ? ["python-m-no-package-spec"] : []);
	let nodeArgs = null;
	if (base === "npx" || base === "bunx") nodeArgs = args;
	else if (base === "bun" && args[0] === "x") nodeArgs = args.slice(1);
	else if (base === "pnpm" && args[0] === "dlx") nodeArgs = args.slice(1);
	else if (base === "yarn" && args[0] === "dlx") nodeArgs = args.slice(1);
	else if (base === "npm" && args[0] === "exec") nodeArgs = args.slice(1);
	if (base === "bun" && args[0] !== "x") return binary();
	if (nodeArgs) {
		const { spec, rest } = extractNodeSpec(nodeArgs);
		if (!spec) return binary();
		if (BRIDGE_PACKAGES.has(specName(spec))) {
			const target = rest.find((a) => /^https?:\/\//i.test(a));
			const targetHost = target ? hostOf(target) : null;
			if (target && targetHost && LOCAL_HOSTS.has(targetHost)) {
				const sse = urlPathEndsWithSse(target) ? ["sse-endpoint"] : [];
				return {
					ok: true,
					name,
					transport: {
						kind: "http",
						url: target
					},
					flags: [
						...flags,
						"unwrapped-bridge",
						...sse
					]
				};
			}
			return {
				ok: false,
				error: bridgeError(targetHost ?? "unknown host")
			};
		}
		return packaged("node", spec, rest);
	}
	let pyArgs = null;
	if (base === "uvx") pyArgs = args;
	else if (base === "uv" && args[0] === "tool" && args[1] === "run") pyArgs = args.slice(2);
	else if (base === "uv" && (args[0] === "run" || args[0] === "x")) {
		if (args.includes("python") || args.includes("python3")) return binary(["uv-run-wraps-python"]);
		pyArgs = args.slice(1);
	} else if (base === "pipx" && args[0] === "run") pyArgs = args.slice(1);
	if (base === "uv" && pyArgs === null) return binary();
	if (pyArgs) {
		const { spec, rest, flags: pyFlags } = extractPySpec(pyArgs);
		if (!spec) return binary(pyFlags);
		return packaged("python", spec, rest, pyFlags);
	}
	return binary();
}
function parseEntry(key, raw) {
	const urlRaw = URL_FIELDS.map((f) => raw[f]).find((v) => typeof v === "string" && v.trim() !== "");
	const typeStr = String(raw.type ?? raw.transport ?? "").toLowerCase();
	if (urlRaw) return parseHttp(key, raw, urlRaw, typeStr);
	if (REMOTE_TYPES.has(typeStr)) return {
		ok: false,
		error: NO_URL_OR_COMMAND_ERROR
	};
	const rawCmd = raw.command ?? raw.cmd;
	const env = coerceStringMap(ENV_FIELDS.map((f) => raw[f]).find(isRecord));
	if (Array.isArray(rawCmd)) {
		const [cmd, ...args] = rawCmd.map(String);
		return parseLocal(key, {
			cmd: cmd ?? "",
			args,
			env
		});
	}
	if (typeof rawCmd === "string" && rawCmd.trim()) return parseLocal(key, {
		cmd: rawCmd,
		args: Array.isArray(raw.args) ? raw.args.map(String) : [],
		env
	});
	return {
		ok: false,
		error: NO_URL_OR_COMMAND_ERROR
	};
}
function looksLikeEntry(value) {
	if (!isRecord(value)) return false;
	return typeof value.command === "string" || Array.isArray(value.command) || typeof value.cmd === "string" || typeof value.type === "string" || URL_FIELDS.some((f) => typeof value[f] === "string");
}
/** Resolve any envelope down to `[name, entry]` pairs; null = nothing recognizable. */
function resolveEntries(parsed) {
	for (const key of ENVELOPE_KEYS) {
		let servers = parsed[key];
		if (servers === void 0) continue;
		if (isRecord(servers) && isRecord(servers.servers)) servers = servers.servers;
		if (Array.isArray(servers)) return servers.filter(isRecord).map((e) => [String(e.name ?? ""), e]);
		if (isRecord(servers)) return Object.entries(servers).filter((pair) => pair[0] !== "inputs" && pair[0] !== "sandbox" && isRecord(pair[1]));
	}
	if (looksLikeEntry(parsed)) return [["", parsed]];
	const pairs = Object.entries(parsed).filter((pair) => looksLikeEntry(pair[1]));
	if (pairs.length > 0 && pairs.length === Object.keys(parsed).length) return pairs;
	return null;
}
function parseMcpServersJson(text, opts) {
	let parsed;
	try {
		parsed = JSON.parse(text);
	} catch {
		const trimmed = text.trim();
		if (trimmed && !/^[{["]/.test(trimmed)) {
			const [cmd, ...args] = shellSplit(trimmed);
			return parseLocal("", {
				cmd: cmd ?? "",
				args,
				env: {}
			});
		}
		return {
			ok: false,
			error: INVALID_JSON_ERROR
		};
	}
	if (typeof parsed === "string") {
		const [cmd, ...args] = shellSplit(parsed);
		return parseLocal("", {
			cmd: cmd ?? "",
			args,
			env: {}
		});
	}
	if (!isRecord(parsed)) return {
		ok: false,
		error: NO_ENTRY_ERROR
	};
	const entries = resolveEntries(parsed);
	if (!entries || entries.length === 0) return {
		ok: false,
		error: NO_ENTRY_ERROR
	};
	if (entries.length > 1) {
		const picked = opts?.pick ? entries.find(([name]) => name === opts.pick) : void 0;
		if (!picked) return {
			ok: false,
			choices: entries.map(([name]) => name)
		};
		return parseEntry(picked[0], picked[1]);
	}
	return parseEntry(entries[0][0], entries[0][1]);
}
//#endregion
//#region src/contract.ts
/**
* The template a server was installed from. `slug`/`name` derive from user
* input and must never be used for identity — renames escape them, unrelated
* servers collide. Bundled `serverId` covers entries saved before `template`.
*/
function templateIdForServer(server) {
	return server.template ?? (server.transport.kind === "bundled" ? server.transport.serverId : void 0);
}
const NAME_MAX = 64;
const DESCRIPTION_MAX = 256;
const SERVER_SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;
const LOOPBACK_HOSTNAMES = /* @__PURE__ */ new Set([
	"localhost",
	"127.0.0.1",
	"::1",
	"[::1]"
]);
function isLoopbackUrl(url) {
	try {
		return LOOPBACK_HOSTNAMES.has(new URL(url).hostname);
	} catch {
		return false;
	}
}
function isLocalMcpUrl(value) {
	const trimmed = value.trim();
	if (!trimmed) return false;
	return isLoopbackUrl(trimmed.includes("://") ? trimmed : `http://${trimmed}`);
}
/**
* Registry a typed package spec unambiguously belongs to, or null when a bare
* name could be either. Deliberately conservative: `name@1.2.3` stays null
* because uv accepts `@` version pins too.
*/
function detectRuntime(spec) {
	const trimmed = spec.trim();
	if (trimmed.startsWith("@")) return "node";
	if (/==|>=|<=|~=/.test(trimmed)) return "python";
	if (/\[[^\]]*\]$/.test(trimmed)) return "python";
	return null;
}
function slugifyServerName(name) {
	return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64).replace(/-+$/, "") || "server";
}
function uniqueSlug(base, taken) {
	if (!taken.has(base)) return base;
	for (let n = 2;; n += 1) {
		const candidate = `${base}-${n}`;
		if (!taken.has(candidate)) return candidate;
	}
}
function previewSlug(name, existingSlugs, reservedSlugs = []) {
	return uniqueSlug(slugifyServerName(name), /* @__PURE__ */ new Set([...existingSlugs, ...reservedSlugs]));
}
function validateServerEntryInput(input) {
	const errors = [];
	if (!input.name.trim()) errors.push("Name is required.");
	if (input.name.length > 64) errors.push(`Name must be at most 64 characters.`);
	if ((input.description ?? "").length > 256) errors.push(`Description must be at most 256 characters.`);
	if (input.transport.kind === "stdio") {
		if (!input.transport.packageSpec.trim()) errors.push("Package is required.");
	} else if (input.transport.kind === "binary") {
		if (!input.transport.command.trim()) errors.push("Command is required.");
	} else if (input.transport.kind === "bundled") {
		if (!input.transport.serverId.trim()) errors.push("Bundled server id is required.");
	} else if (!isLoopbackUrl(input.transport.url)) errors.push("Server URL must point to localhost — remote MCP servers are added under Integrations.");
	return errors;
}
//#endregion
export { isLocalMcpUrl as a, slugifyServerName as c, parseMcpServersJson as d, detectRuntime as i, templateIdForServer as l, NAME_MAX as n, isLoopbackUrl as o, SERVER_SLUG_RE as r, previewSlug as s, DESCRIPTION_MAX as t, validateServerEntryInput as u };

//# sourceMappingURL=contract-B9iEN9f6.js.map